import React, { Suspense, lazy, useMemo } from "react"
import { ThemeProvider } from "@mui/material/styles"
import { CssBaseline, Box, CircularProgress, Typography, Button } from "@mui/material"
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom"
import { AnimatePresence } from "framer-motion"
import { MainLayout } from "./components/MainLayout"
import { ErrorBoundary } from "./components/ErrorBoundary"
import { AuthInitializer } from "./components/AuthInitializer"
import { ScrollToTop } from "./components/ScrollToTop"
import { PageTransition } from "./components/PageTransition"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { SnackbarProvider } from "notistack"
import { ColorModeProvider, useColorMode } from "./theme/colorMode"
import { getAppTheme } from "./theme/theme"

// Компонент-заглушка для ошибок загрузки
const ErrorFallback: React.FC = () => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			minHeight: "50vh",
			gap: 2,
		}}
	>
		<Typography variant="h6" color="error">
			Ошибка загрузки компонента
		</Typography>
		<Button
			variant="contained"
			onClick={() => window.location.reload()}
		>
			Обновить страницу
		</Button>
	</Box>
)

// Lazy-loaded pages for code splitting with error handling
const lazyWithErrorBoundary = <T extends React.ComponentType<any>>(
	importFn: () => Promise<{ default: T }>
) => {
		return lazy(() =>
		importFn().catch((error) => {
			console.error("Failed to load component:", error)
			// Возвращаем компонент-заглушку вместо падения приложения
			return {
				default: ErrorFallback as unknown as T,
			}
		})
	)
}

const PolicyForm = lazyWithErrorBoundary(() => import("./components/PolicyForm"))
const GlassAuth = lazyWithErrorBoundary(() => import("./components/GlassAuth"))
const HomePage = lazyWithErrorBoundary(() =>
	import("./pages/HomePage").then((m) => ({ default: m.HomePage }))
)
const ProfilePage = lazyWithErrorBoundary(() =>
	import("./pages/ProfilePage").then((m) => ({ default: m.ProfilePage }))
)
const VerifyEmailPage = lazyWithErrorBoundary(() =>
	import("./pages/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage }))
)
const PaidPoliciesPage = lazyWithErrorBoundary(() =>
	import("./pages/PaidPoliciesPage").then((m) => ({ default: m.PaidPoliciesPage }))
)
const PaymentSuccessPage = lazyWithErrorBoundary(() => import("./pages/PaymentSuccessPage"))
const PaymentFailPage = lazyWithErrorBoundary(() => import("./pages/PaymentFailPage"))
const TariffsPage = lazyWithErrorBoundary(() =>
	import("./pages/TariffsPage").then((m) => ({ default: m.TariffsPage }))
)
const AdminDashboard = lazyWithErrorBoundary(() =>
	import("./pages/AdminDashboard").then((m) => ({ default: m.AdminDashboard }))
)
const VerifyEmailSentPage = lazyWithErrorBoundary(() =>
	import("./pages/VerifyEmailSentPage").then((m) => ({ default: m.VerifyEmailSentPage }))
)

const AnimatedRoutes: React.FC = () => {
	const location = useLocation()

	return (
		<AnimatePresence mode="wait">
			<Routes
				location={location}
				key={location.pathname}
			>
				<Route element={<MainLayout />}>
					<Route
						path="/"
						element={
							<PageTransition>
								<HomePage />
							</PageTransition>
						}
					/>
					<Route
						path="/policy"
						element={
							<PageTransition>
								<PolicyForm />
							</PageTransition>
						}
					/>
					<Route
						path="/profile"
						element={
							<PageTransition>
								<ProfilePage />
							</PageTransition>
						}
					/>
					<Route
						path="/verify-email"
						element={
							<PageTransition>
								<VerifyEmailPage />
							</PageTransition>
						}
					/>
					<Route
						path="/verify-email-sent"
						element={
							<PageTransition>
								<VerifyEmailSentPage />
							</PageTransition>
						}
					/>
					<Route
						path="/policies"
						element={
							<PageTransition>
								<PaidPoliciesPage />
							</PageTransition>
						}
					/>
					<Route
						path="/tariffs"
						element={
							<PageTransition>
								<TariffsPage />
							</PageTransition>
						}
					/>
					<Route
						path="/admin"
						element={
							<PageTransition>
								<AdminDashboard />
							</PageTransition>
						}
					/>
					<Route
						path="/payment/success"
						element={
							<PageTransition>
								<PaymentSuccessPage />
							</PageTransition>
						}
					/>
					<Route
						path="/payment/fail"
						element={
							<PageTransition>
								<PaymentFailPage />
							</PageTransition>
						}
					/>
				</Route>
				<Route
					path="/auth/login"
					element={
						<PageTransition>
							<GlassAuth />
						</PageTransition>
					}
				/>
				<Route
					path="/auth/register"
					element={
						<PageTransition>
							<GlassAuth />
						</PageTransition>
					}
				/>
				<Route
					path="*"
					element={
						<Navigate
							to="/"
							replace
						/>
					}
				/>
			</Routes>
		</AnimatePresence>
	)
}

// Loading fallback component
const PageLoader = () => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			minHeight: "50vh",
		}}
	>
		<CircularProgress sx={{ color: "primary.main" }} />
	</Box>
)

// Создаём клиент React Query с настройками кэширования
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000, // 5 минут - данные считаются свежими
			gcTime: 10 * 60 * 1000, // 10 минут - храним в кэше
			refetchOnWindowFocus: true, // Обновлять при фокусе окна для синхронизации между вкладками
			refetchOnMount: true, // Перезапрашивать при монтировании для проверки актуальности
			refetchOnReconnect: true, // Обновлять при восстановлении сети
			retry: (failureCount, error: any) => {
				// Не повторяем при 401 - interceptor обработает это
				if (error?.response?.status === 401) {
					return false
				}
				// Повторяем другие ошибки максимум 1 раз
				return failureCount < 1
			},
			// Важно: дедупликация запросов
			networkMode: "online", // Только при наличии сети
		},
	},
})

const AppShell: React.FC = () => {
	const { mode } = useColorMode()
	const theme = useMemo(() => getAppTheme(mode), [mode])

	return (
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<SnackbarProvider
					maxSnack={3}
					anchorOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
					autoHideDuration={4000}
				>
					<ThemeProvider theme={theme}>
						<CssBaseline />
						{/* AuthInitializer восстанавливает сессию из httpOnly cookie при перезагрузке */}
						<AuthInitializer>
							<Box
								component="div"
								sx={(t) => ({
									minHeight: "100vh",
									background: t.palette.background.default,
									color: t.palette.text.primary,
									overflowX: "hidden",
									width: "100%",
									maxWidth: "100vw",
									"& *": { boxSizing: "border-box" },
								})}
							>
								<BrowserRouter>
									<ScrollToTop />
									<Suspense fallback={<PageLoader />}>
										<AnimatedRoutes />
									</Suspense>
								</BrowserRouter>
							</Box>
						</AuthInitializer>
					</ThemeProvider>
				</SnackbarProvider>
			</QueryClientProvider>
		</ErrorBoundary>
	)
}

const App: React.FC = () => {
	return (
		<ColorModeProvider>
			<AppShell />
		</ColorModeProvider>
	)
}

export default App
